import React, { useContext } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectFade, Grid, Navigation } from 'swiper/modules';
import 'swiper/css/bundle';
import 'swiper/css';
import 'swiper/css/grid';
import 'swiper/css/navigation';
import { AsgFwContext, AsgDictionaryContext } from '../../../../contexts';
import { GamesService } from '../../../../../../shared/src/services/games.service';
import { useSelector, useDispatch } from 'react-redux';
import { useAsgActions } from '../../../../../../shared/src/hooks/use-asg-actions.hook';
import '../RecommendationsCarousel/RecommendationsCarousel.css';
import { useTranslations } from '../../../../../../shared/src/hooks/use-translations.hook';
import { PerfUtilService } from '../../../../../../shared/src/services/util/perf-util.service';
import { verticalActionTypesEnum } from '../../../../../../shared/src/enums/vertical-types.enum';
import { GridMessage } from '../../GridMessage';
import {
    GtmEventComponent,
    GtmEventLocation
} from '../../../../../../shared/src/components/google-tag-manager/enums/gtm-event-enum';
import { useCurrentVerticalData } from '../../../../../../shared/src/hooks/use-current-vertical-data.hook';
import { useSwiperTracking } from '../../../../../../shared/src/hooks/use-swiper-tracking.hook';

export const RecommendationsCarousel = ({
    index,
    liveCasinoData,
    gamesCategory,
}) => {
    const maxGamesInSliderRow = 20;
    const maxGamesInSlider = 50;
    const AsgFW = useContext(AsgFwContext);
    const Dictionary = useContext(AsgDictionaryContext);
    const {
        session,
        balance: {
            Currency,
            CurrencySign,
        },
        recentGames,
    } = useSelector((state) => state.player);
    const { isMobile, regulation, areFavoritesEnabled } = useSelector((state) => state.app);
    const { buyWin, gamesById } = useSelector(state => state.casino);
    const { goTo, loadGame } = useAsgActions();
    const dispatch = useDispatch();
    const { t } = useTranslations();
    const { favoriteGames } = useSelector(state => state.favorites);
    const hasFavoritesGames = favoriteGames.length;
    let messageText = null;

    const name = gamesCategory.category.name;
    const isFavoritesCategory = areFavoritesEnabled && name === verticalActionTypesEnum.FAVORITES_CATEGORY;
    const translatedName = !isFavoritesCategory
        ? GamesService.replaceGameName(
            t(name),
            gamesCategory.category?.gameId,
            gamesById,
        )
        : t('favorites');

    let gameIds = gamesCategory.games.map(game => game.gameId);

    const {
        pamGameVerticalID,
        currentVerticalType,
    } = useCurrentVerticalData();

    const {
        onSlideChangeHandler,
    } = useSwiperTracking({
        location: GtmEventLocation.GAME_GRID,
        component: GtmEventComponent.GAME_GRID,
        game_vertical_id: pamGameVerticalID,
        game_vertical_type: currentVerticalType,
        game_category: name,
    });

    if (isFavoritesCategory && hasFavoritesGames) {
        gameIds = favoriteGames;
    }

    if (!isFavoritesCategory && (!Array.isArray(gameIds) || !gameIds.length)) {
        return null;
    }

    const gamesToRender = GamesService.filterGamesToRender(gameIds, AsgFW).slice(0, maxGamesInSlider);

    if (!isFavoritesCategory && !gamesToRender.length) {
        return null;
    } else if (!gamesToRender.length) {
        messageText = 'favoritesEmpty';
    }

    const isTwoRowsSlider = gamesToRender.length > maxGamesInSliderRow;
    const sliderRows = isTwoRowsSlider ? 2 : 1;

    // hide extra slider (we have 2 sliders for 1 and 2 rows, that showed in appropriate games number)
    // for favorite games
    if (isFavoritesCategory && gamesCategory.category.rows !== sliderRows) {
        return null;
    }

    const onSlideChange = (swiper) => {
        if (!isMobile) {
            if (swiper.navigation.$nextEl) {
                if (swiper.navigation.$nextEl[0].classList.contains('swiper-button-disabled')) {
                    swiper.$el[0].classList.remove('swiper-next-button-gradient');
                } else if (swiper.$el && !swiper.$el[0].classList.contains('swiper-button-disabled')) {
                    swiper.$el[0].classList.add('swiper-next-button-gradient');
                }
            }

            if (swiper.navigation.$prevEl) {
                if (swiper.navigation.$prevEl[0].classList.contains('swiper-button-disabled')) {
                    swiper.$el[0].classList.remove('swiper-prev-button-gradient');
                } else if (swiper.$el && !swiper.$el[0].classList.contains('swiper-button-disabled')) {
                    swiper.$el[0].classList.add('swiper-prev-button-gradient');
                }
            }
        }

        onSlideChangeHandler();
    };

    const onResize = PerfUtilService.debounce((swiper) => {
        swiper.slideTo(0);
        onSlideChange(swiper);
    }, 300);

    const navEnabled = (gamesCountLimit) => !isMobile && gamesToRender.length >= gamesCountLimit;
    const breakpointsProps = {
        navigation: {
            nextEl: `#recommended-swiper-${index} .swiper-button-next`,
            prevEl: `#recommended-swiper-${index} .swiper-button-prev`,
        },
        common: {
            grid: {
                rows: sliderRows,
                fill: 'row',
            },
            slidesPerGroup: isMobile ? 1 : 2, // two thumbnails per one slide
        },
    };

    return (
        <div className='recommendations-games-container'>
            <div className='recommendations-games-container-block'>
                <div className='recommendations-games-title'>
                    <div className='recommendations-games-title-text'>
                        {translatedName}
                    </div>
                </div>
                <GridMessage messageText={messageText} />
                <div className='recommendations-games-carousel-container' id={`recommended-swiper-${index}`}>
                    <Swiper
                        modules={[Navigation, EffectFade, Grid]}
                        slidesPerView={2.4}
                        spaceBetween={16}
                        navigation={{
                            enabled: gamesToRender.length > 1,
                            ...breakpointsProps.navigation,
                        }}
                        breakpoints={{
                            320: {
                                spaceBetween: 16,
                                slidesPerView: 1.8,
                                navigation: {
                                    enabled: navEnabled(2),
                                    ...breakpointsProps.navigation,
                                },
                                ...breakpointsProps.common,
                            },
                            460: {
                                spaceBetween: 16,
                                slidesPerView: 2.4,
                                navigation: {
                                    enabled: navEnabled(3),
                                    ...breakpointsProps.navigation,
                                },
                                ...breakpointsProps.common,
                            },
                            760: {
                                spaceBetween: 16,
                                slidesPerView: 3.4,
                                navigation: {
                                    enabled: navEnabled(4),
                                    ...breakpointsProps.navigation,
                                },
                                ...breakpointsProps.common,
                            },
                            1024: {
                                spaceBetween: 16,
                                slidesPerView: 4.4,
                                navigation: {
                                    enabled: navEnabled(5),
                                    ...breakpointsProps.navigation,
                                },
                                ...breakpointsProps.common,
                            },
                            1280: {
                                spaceBetween: 20,
                                slidesPerView: 5.4,
                                navigation: {
                                    enabled: navEnabled(6),
                                    ...breakpointsProps.navigation,
                                },
                                ...breakpointsProps.common,
                            },
                            1440: {
                                spaceBetween: 20,
                                slidesPerView: 6.4,
                                navigation: {
                                    enabled: navEnabled(7),
                                    ...breakpointsProps.navigation,
                                },
                                ...breakpointsProps.common,
                            },
                            1660: {
                                spaceBetween: 20,
                                slidesPerView: 7.4,
                                navigation: {
                                    enabled: navEnabled(8),
                                    ...breakpointsProps.navigation,
                                },
                                ...breakpointsProps.common,
                            },
                        }}
                        onSlideChange={onSlideChange}
                        onInit={(swiper) => {
                            setTimeout(() => onSlideChange(swiper), 0);
                        }}
                        onResize={onResize}
                        onTouchStart={onSlideChange}
                        onSlideChangeTransitionEnd={onSlideChangeHandler}
                        grid={breakpointsProps.grid}
                        watchSlidesProgress={true}
                        effect="slide"
                        fadeEffect={{ crossFade: true }}
                    >
                        {gamesToRender.map((gameId, index) => <SwiperSlide key={index}>
                            {GamesService.renderCasinoThumbnail({
                                index,
                                gameId,
                                AsgFW,
                                regulation,
                                Currency,
                                CurrencySign,
                                liveCasinoData,
                                session,
                                isMobile,
                                recentGames,
                                loadGame,
                                goTo,
                                buyWin,
                                dispatch,
                                Dictionary,
                            })}
                        </SwiperSlide>
                        )}
                    </Swiper>
                    <div className='swiper-button-prev swiper-button-disabled'></div>
                    <div className='swiper-button-next swiper-button-disabled'></div>
                </div>
            </div>
        </div>
    );
};
